import {
  useState,
  useContext,
  useEffect,
  useMemo,
  lazy,
  Suspense,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';

import {
  setCartItemCount,
} from '#features/misc/misc.slice';
import {
  useGetCartMutation,
  useRemoveFromCartMutation,
} from '#services/cart';
import {
  useCheckoutOrdersMutation,
} from '#services/order';
import {
  formatCurrency,
  formatDate,
  getCustomizationConfirmDays,
} from '#common/utils';
import { ORDER_TYPES } from '#common/constants';
import { ADDONS } from '#common/constants/addons';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import CheckoutUser from './CheckoutUser';

interface CartProduct {
  id: string | number;
  name: string;
  version: number | string;
  inactivated?: boolean;
  collectionIds: Array<string | number>;
  shippingWindow?: Array<{ name: string; weeks: number; price: number }>;
  customizations: Array<{ name: string; price: number }>;
  [key: string]: unknown;
}

interface CartItem {
  id: string | number;
  product: CartProduct;
  productVersion: number | string;
  inStock?: boolean;
  fromInventory?: boolean;
  estimateShipDate: string | number | Date;
  shipDatePending?: boolean;
  wearDate: string | number | Date;
  selectedShippingWindow: number;
  selectedCustomizations?: Record<string, unknown>;
  customizationNotes: Record<string, unknown>;
  specialRequests?: string;
  unitPrice: number;
  totalPrice: number;
  amounts: number;
  size?: string;
  color?: string;
  poNumber?: string;
  productId?: string | number;
  [key: string]: unknown;
}

interface CartCollection {
  id: string | number;
  name: string;
  availableForAll?: boolean;
  [key: string]: unknown;
}

interface CartState {
  items: CartItem[];
  collections: CartCollection[];
}

interface ItemOverride {
  id: string | number;
  amounts?: number;
}

interface UnavailableItem {
  productId?: string | number;
  color?: string;
  size?: string;
  [key: string]: unknown;
}

interface DiscountInfo {
  totalDiscount: number;
  [key: string]: unknown;
}

interface AppliedCoupon {
  code: string;
  [key: string]: unknown;
}

// Lazy load CouponCheckout component
const CouponCheckout = lazy(() =>
  (import('#addons/promotions/CouponCheckout') as unknown as Promise<{ default: React.ComponentType<Record<string, unknown>> }>)
    .catch(() => ({ default: () => null })));

function isItemAvailable(item: CartItem) {
  const { product } = item;
  const isVersionChanged = (item.productVersion !== product.version);
  const isInactivated = product.inactivated;

  return !(isVersionChanged || isInactivated);
}

// TODO reuse the order table with the cart page?

export default function CartList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const { t } = useTranslation('cart');
  const [state, setState] = useState<CartState>({ items: [], collections: [] });
  const [unavailableItems, setUnavailableItems] = useState<UnavailableItem[]>([]);
  const [checkoutAsUserId, setCheckoutAsUserId] = useState<string | number>('');
  const [checkoutAsError, setCheckoutAsError] = useState(false);
  const [appliedCoupons, setAppliedCoupons] = useState<AppliedCoupon[]>([]);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [itemOverrides, setItemOverrides] = useState<ItemOverride[]>(() => {
    const saved = localStorage.getItem('cartItemOverrides');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [deleteCartItem] = useRemoveFromCartMutation();
  const [getCart] = useGetCartMutation();
  const [checkoutOrders] = useCheckoutOrdersMutation();
  const userInfo = useSelector(
    (s: { auth: { userInfo: { isCustomer?: boolean; id?: string | number } } }) => s.auth.userInfo,
  );
  const customizationConfirmDays = useSelector(
    (s: { appConfig: { customizationConfirmDays?: number | string } }) => getCustomizationConfirmDays(s.appConfig),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  const refreshCart = () => {
    showBackdrop();
    setUnavailableItems([]);
    void getCart(undefined).then((res) => {
      hideBackdrop();
      if (res.error) {
        return;
      }
      setState(res.data as CartState);
    });
  };

  useEffect(() => {
    refreshCart();
  }, []);

  const subtotal = useMemo(() => {
    return state.items.reduce((total, item) => {
      if (!isItemAvailable(item)) {
        return total;
      }

      // check the item overrides
      const override = itemOverrides.find((io) => io.id === item.id);
      return total + (
        override
          ? (item.unitPrice * override.amounts!)
          : item.totalPrice
      );
    }, 0);
  }, [state, itemOverrides]);

  const isInventory = location.search.includes('inventory=true');

  const handleSubmit = (orderType: number) => {
    if (checkoutAsUserId === '' && !userInfo.isCustomer) {
      setCheckoutAsError(true);
      return;
    }

    showBackdrop();
    setUnavailableItems([]);
    void checkoutOrders({
      checkoutAsUserId,
      itemOverrides,
      appliedCouponCodes: appliedCoupons.map((c) => c.code),
    })
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], t('snackbar.orderAdded'));
        if (hasError) {
          const items = (res as { error?: { data?: { unavailableItems?: UnavailableItem[] } } }).error?.data?.unavailableItems;
          if (items) {
            setUnavailableItems(items);
          }
          return;
        }

        // Clear applied coupons on successful checkout
        setAppliedCoupons([]);
        setDiscountInfo(null);

        setState({
          ...state,
          items: [],
        });

        const resData = (res as { data: { cartItemCount: number; orderId: string | number } }).data;
        dispatch(setCartItemCount(resData.cartItemCount));

        // Clear the item overrides from localStorage
        localStorage.removeItem('cartItemOverrides');

        let redirectTo = null;
        if (orderType === ORDER_TYPES.CUSTOMER_ORDER) {
          redirectTo = '/shop';
        } else if (orderType === ORDER_TYPES.ADMIN_ORDER) {
          redirectTo = `/management/order/detail?orderId=${resData.orderId}`;
        } else if (orderType === ORDER_TYPES.INVENTORY_ORDER) {
          redirectTo = '/management/inventory';
        }
        if (redirectTo) {
          void navigate(redirectTo);
        }
      }).finally(() => {
        hideBackdrop();
      });
  };

  const nonItemAvailable = state.items.every((item) => !isItemAvailable(item));

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: '',
    label: t('table.item'),
    renderer: (row) => {
      const item = row as unknown as CartItem;
      const { product } = item;

      const wearDate = formatDate(item.wearDate);

      let shippingWindowText = '';
      let estimateShipDateText = t('item.inStockShipNow');
      if (item.shipDatePending) {
        estimateShipDateText = userInfo.isCustomer
          ? t('item.shipDatePending')
          : t('item.shipDateSetAfterOrder');
      } else if (!item.inStock) {
        estimateShipDateText = formatDate(new Date(item.estimateShipDate));
      }

      if (!item.fromInventory && product.shippingWindow) {
        const current = product.shippingWindow[item.selectedShippingWindow];
        if (current) {
          shippingWindowText = t('item.shippingWindow', {
            name: current.name,
            weeks: current.weeks,
            price: formatCurrency(current.price),
          });
        }
      }

      const inventoryQueryParam = isInventory
        ? '&inventory=true'
        : '';

      const collectionNames = item.product.collectionIds.map((cid) => {
        const find = state.collections.find((col) => col.id === cid);
        return find!.name;
      }).join(', ');

      return (
        <>
          <Box>
            <Link
              component="button"
              onClick={() => { void navigate(`/shop/add?productId=${product.id}${inventoryQueryParam}`); }}
            >
              {product.name}
            </Link>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.collection')}</b>
              :
              {' '}
              {collectionNames}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.size')}</b>
              :
              {' '}
              {item.size}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.color')}</b>
              :
              {' '}
              {item.color}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.wearDate')}</b>
              :
              {' '}
              {wearDate}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.poNumber')}</b>
              :
              {' '}
              {item.poNumber}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.estimateShipDate')}</b>
              :
              {' '}
              {item.shipDatePending ? (
                <Box component="span" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {estimateShipDateText}
                </Box>
              ) : estimateShipDateText}
            </Typography>
          </Box>
          <Box>
            {shippingWindowText}
            {
              Object.keys(item.selectedCustomizations || {})
                .filter((k) => (item.selectedCustomizations as Record<string, unknown>)[k])
                .map((scIdx, idx) => {
                  const custom = product.customizations[scIdx as unknown as number]!;
                  return (
                    <Box key={idx}>
                      <Typography variant="body2">
                        {custom.name}
                        &nbsp;(+
                        {formatCurrency(custom.price)}
                        ) &nbsp;
                        {(item.customizationNotes)[scIdx] as string}
                      </Typography>
                    </Box>
                  );
                })
            }
            {item.specialRequests ? (
              <Box>
                <Typography variant="body2">
                  <b>{t('item.specialRequests')}</b>
                  :
                  {' '}
                  {item.specialRequests}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </>
      );
    },
  }, {
    id: 'amounts',
    label: t('table.qty'),
    renderer: (row) => {
      const item = row as unknown as CartItem;
      const scope = 'amounts';
      if (!isInventory) {
        return item[scope];
      }

      // Get the saved override value if it exists
      const savedOverride = itemOverrides.find(io => io.id === item.id);
      const defaultValue = savedOverride ? savedOverride.amounts : item[scope];

      return (
        <TextField
          sx={{ width: 85 }}
          type="number"
          size="small"
          name={scope}
          value={defaultValue}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            if (nextValue < 1) {
              event.target.value = '1';
              return;
            }

            let current = itemOverrides.find((itm) => itm.id === item.id);
            if (!current) {
              current = { id: item.id };
              itemOverrides.push(current);
            }
            current.amounts = nextValue;
            const newOverrides = [...itemOverrides];
            setItemOverrides(newOverrides);
            localStorage.setItem('cartItemOverrides', JSON.stringify(newOverrides));
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );
    },
  }, {
    id: 'unitPrice',
    label: t('table.unitPrice'),
    renderer: (row) => `${formatCurrency((row as unknown as CartItem).unitPrice)}`,
  }, {
    id: 'totalPrice',
    label: t('table.total'),
    renderer: (row) => {
      const item = row as unknown as CartItem;
      const override = itemOverrides.find((io) => io.id === item.id);

      const totalPrice = override
        ? override.amounts! * item.unitPrice
        : item.totalPrice;

      return `${formatCurrency(totalPrice)}`;
    },
  }, {
    id: 'action',
    label: t('table.action'),
    renderer: (row) => {
      const item = row as unknown as CartItem;
      const handleDeleteItem = () => {
        setDialogData({
          title: t('delete.title'),
          text: t('delete.text'),
          disagree: t('delete.cancel'),
          agree: t('delete.confirm'),
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            showBackdrop();

            void deleteCartItem({ id: item.id })
              .then((res) => {
                const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], t('snackbar.itemRemoved'));
                if (!hasError) {
                  // Remove the override for this item from localStorage
                  const newOverrides = itemOverrides.filter(io => io.id !== item.id);
                  setItemOverrides(newOverrides);
                  localStorage.setItem('cartItemOverrides', JSON.stringify(newOverrides));
                  refreshCart();
                }
              });
          },
        });
        showDialog();
      };
      return (
        <Link component="button" onClick={handleDeleteItem}>{t('action.del')}</Link>
      );
    },
  }];

  const isItemUnavailable = (item: CartItem) => {
    return unavailableItems.some(ui =>
      ui.productId === item.productId && ui.color === item.color && ui.size === item.size
    );
  };

  const banner = {
    renderer: (row: EnhancedTableRow) => {
      const item = row as unknown as CartItem;
      const {
        product,
        estimateShipDate,
        wearDate,
      } = item;

      const isVersionChanged = (item.productVersion !== product.version);
      const isInactivated = product.inactivated;

      // for a pending item estimateShipDate is the computed floor checkout
      // still validates the wear date against
      const isShipDateConflict = (new Date(estimateShipDate) > new Date(wearDate));
      return (
        <>
          {
            isItemUnavailable(item)
              && (<Alert severity="error">{t('alert.outOfStock')}</Alert>)
          }
          {
            isVersionChanged
              && (<Alert severity="error">{t('alert.versionChanged')}</Alert>)
          }
          {
            !isVersionChanged && isInactivated
              && (<Alert severity="warning">{t('alert.inactivated')}</Alert>)
          }
          {
            isShipDateConflict
              && (<Alert severity="error">{t('alert.wearDateConflict')}</Alert>)
          }
          {
            item.shipDatePending && userInfo.isCustomer
              && (<Alert severity="info">{t('alert.customizationConfirm', { count: customizationConfirmDays })}</Alert>)
          }
        </>
      );
    },
  };

  const getRowStyle = (row: EnhancedTableRow) => {
    if (isItemUnavailable(row as unknown as CartItem)) {
      return { bgcolor: '#fff5f5' };
    }
    return {};
  };

  const tableProps: Omit<EnhancedTableProps, 'name'> = {
    banner,
    headers: tableHeaders,
    getRowStyle,

    enableSort: false,

    rows: state.items,
  };

  return (
    <Grid container>
      <Grid size={12}>
        <Box sx={{ pt: { xs: 1, sm: 2 } }}>
          <Alert variant="outlined" severity="info">
            {t('retentionNotice')}
          </Alert>
        </Box>
        <Box sx={{ py: 2 }}>
          <Typography variant={'h7' as TypographyProps['variant']} gutterBottom>
            {t('checkout')}
          </Typography>
          <Divider sx={{ opacity: 0.6 }} />
        </Box>

        <Box sx={{ pb: { xs: 3, sm: 5 } }}>
          <EnhancedTable
            name="variants"
            {...tableProps}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              pt: 2,
              gap: 0.5,
            }}
          >
            <Box>
              <b>{t('summary.subtotal')}</b>
              :
              {' '}
              <span data-testid="subtotal">{formatCurrency(subtotal)}</span>
            </Box>
            {discountInfo && discountInfo.totalDiscount > 0 && (
              <Box sx={{ color: 'success.main' }}>
                <b>{t('summary.discount')}</b>
                :
                {' '}
                <span data-testid="discount">-{formatCurrency(discountInfo.totalDiscount)}</span>
              </Box>
            )}
            {discountInfo && discountInfo.totalDiscount > 0 && (
              <Box sx={{ fontSize: '1.1rem', borderTop: '1px solid', borderColor: 'divider', pt: 0.5, mt: 0.5 }}>
                <b>{t('summary.total')}</b>
                :
                {' '}
                <span data-testid="total">{formatCurrency(subtotal - discountInfo.totalDiscount)}</span>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ opacity: 0.6 }} />

        {/* Coupon Section - Only show if promotions addon is enabled */}
        {window.subscriptions?.[ADDONS.PROMOTIONS] && !window.subscriptions[ADDONS.PROMOTIONS]?.expired && (
          <Suspense fallback={null}>
            <Box sx={{ py: 2 }}>
              <CouponCheckout
                cartItems={state.items.filter(isItemAvailable)}
                customerId={checkoutAsUserId || (userInfo.isCustomer ? userInfo.id : null)}
                appliedCoupons={appliedCoupons}
                onAppliedCouponsChange={setAppliedCoupons}
                discountInfo={discountInfo}
                onDiscountInfoChange={setDiscountInfo}
              />
            </Box>
            <Divider sx={{ opacity: 0.6 }} />
          </Suspense>
        )}

        <CheckoutUser
          value={checkoutAsUserId}
          error={checkoutAsError}
          hideCustomer={isInventory}
          hideAdmin={!isInventory}
          items={state.items}
          collections={state.collections}
          disabled={nonItemAvailable}
          onChange={(event, customer) => {
            if (customer) {
              setCheckoutAsError(false);
              setCheckoutAsUserId(customer.id as string | number);
            } else {
              setCheckoutAsError(true);
            }
          }}
          onSubmit={handleSubmit}
        />
      </Grid>
    </Grid>
  );
}
